import React, { useState, useEffect } from "react";
import { Bookmark, Plus, Check, Trash2, X } from "lucide-react";
import { pushRecentColor } from "./ColorPicker";

const PALETTE_KEY = "pk-series-palettes";

const loadPalettes = () => {
  try {
    const arr = JSON.parse(localStorage.getItem(PALETTE_KEY) || "[]");
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};
const savePalettes = (arr) => {
  try {
    localStorage.setItem(PALETTE_KEY, JSON.stringify(arr));
  } catch (_) {}
};

/* ------------------------------------------------------------------ */
/*  SeriesPalettes                                                    */
/*  Save current bg + 5 most-recent accent colours under a series     */
/*  name. Apply later to instantly restore the palette.               */
/* ------------------------------------------------------------------ */
const SeriesPalettes = ({ currentBg, onApply }) => {
  const [palettes, setPalettes] = useState(loadPalettes());
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [accents, setAccents] = useState([]);

  const refresh = () => setPalettes(loadPalettes());

  useEffect(() => {
    if (!adding) return;
    // Pre-populate with recents
    try {
      const recent = JSON.parse(localStorage.getItem("pk-recent-colors") || "[]");
      setAccents(Array.isArray(recent) ? recent.slice(0, 5) : []);
    } catch {
      setAccents([]);
    }
  }, [adding]);

  const handleSave = () => {
    if (!name.trim()) return;
    const next = [
      { id: Date.now().toString(), name: name.trim(), bg: currentBg || "#FFFFFF", accents: accents.filter(Boolean) },
      ...palettes,
    ].slice(0, 50);
    savePalettes(next);
    setPalettes(next);
    setName("");
    setAccents([]);
    setAdding(false);
  };

  const handleApply = (p) => {
    // Push accents into recent so they appear in any color picker
    [p.bg, ...(p.accents || [])].forEach((c) => pushRecentColor(c));
    onApply?.(p);
  };

  const handleDelete = (id) => {
    const next = palettes.filter((p) => p.id !== id);
    savePalettes(next);
    setPalettes(next);
  };

  return (
    <div className="space-y-2" data-testid="series-palettes">
      {!adding && (
        <button
          onClick={() => setAdding(true)}
          data-testid="save-palette-btn"
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-dashed border-[#D3D1C7] text-xs text-[#534AB7] hover:bg-[#F1EFE8]"
        >
          <Plus className="w-3 h-3" /> Save current as series
        </button>
      )}
      {adding && (
        <div className="border border-[#D3D1C7] rounded-md p-2.5 space-y-2 bg-[#FAFAF9]">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Series name (e.g. Summer Brain Boost)"
            data-testid="palette-name-input"
            className="w-full text-xs px-2 py-1.5 rounded-md bg-white border border-[#D3D1C7] focus:outline-none focus:border-[#534AB7]"
          />
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-[#5F5E5A]">
            Background
            <span className="ml-auto inline-block w-5 h-5 rounded border border-[#D3D1C7]" style={{ backgroundColor: currentBg || "#FFFFFF" }} />
          </div>
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-[#5F5E5A]">
            Accents (from recents)
            <div className="ml-auto flex gap-1">
              {accents.length === 0 && <span className="text-[#5F5E5A]/60 normal-case tracking-normal">— none —</span>}
              {accents.map((c, i) => (
                <span key={i} className="inline-block w-5 h-5 rounded border border-[#D3D1C7]" style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          <div className="flex gap-1.5">
            <button
              onClick={() => { setAdding(false); setName(""); }}
              className="flex-1 px-2 py-1.5 rounded-md text-xs border border-[#D3D1C7] hover:bg-white"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!name.trim()}
              data-testid="palette-save-confirm-btn"
              className="flex-1 px-2 py-1.5 rounded-md text-xs bg-[#534AB7] text-white hover:bg-[#3C3489] disabled:opacity-50 flex items-center justify-center gap-1"
            >
              <Check className="w-3 h-3" /> Save
            </button>
          </div>
        </div>
      )}
      {palettes.length > 0 && (
        <div className="space-y-1.5 mt-2" data-testid="palette-list">
          {palettes.map((p) => (
            <div key={p.id} className="group flex items-center gap-2 px-2 py-1.5 rounded-md border border-[#D3D1C7] bg-white hover:border-[#534AB7] transition-colors">
              <button
                onClick={() => handleApply(p)}
                data-testid={`palette-apply-${p.id}`}
                className="flex-1 flex items-center gap-2 text-left min-w-0"
              >
                <div className="flex">
                  <span className="w-4 h-4 rounded-l border border-[#D3D1C7]" style={{ backgroundColor: p.bg }} />
                  {(p.accents || []).slice(0, 4).map((c, i, arr) => (
                    <span
                      key={i}
                      className={`w-4 h-4 border border-[#D3D1C7] -ml-px ${i === arr.length - 1 ? "rounded-r" : ""}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-[#2C2C2A] truncate">{p.name}</div>
                  <div className="text-[10px] text-[#5F5E5A]">{(p.accents || []).length} accent{(p.accents || []).length === 1 ? "" : "s"}</div>
                </div>
              </button>
              <button
                onClick={() => handleDelete(p.id)}
                className="opacity-0 group-hover:opacity-100 p-1 rounded text-[#5F5E5A] hover:text-[#BA7517] transition-opacity"
                aria-label={`Delete ${p.name}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {palettes.length === 0 && !adding && (
        <p className="text-[11px] text-[#5F5E5A] italic leading-snug px-1">
          Save your colour scheme as a "Series" to instantly reuse it on the next book in the series.
        </p>
      )}
    </div>
  );
};

export default SeriesPalettes;
